import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaFilter, FaSearch, FaIndustry, FaWrench, FaWater, FaSwimmingPool, FaTools, FaArrowRight } from 'react-icons/fa';
import styled from 'styled-components';
import Seo from '../../../shared/seo/Seo';


import PageHeader from 'features/common/PageHeader';
import { createProjectsData, Project } from '../data/projectsData';
import Newsletter from "../../common/Newsletter";
import { getCanonicalUrl, pageMeta } from 'shared/seo/seoMeta';

interface Category {
  id: string;
  name: string;
  icon: React.ReactElement;
  labelKey: string;
}

interface CategoryButtonProps {
  active: boolean;
}

const Projects: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);


  
  // Project categories with icons
  const categories: Category[] = [
    { id: 'all', name: t('services.categories.all'), icon: <FaIndustry />, labelKey: 'services.categories.all' },
    { id: 'maintenance', name: t('services.categories.maintenance'), icon: <FaWrench />, labelKey: 'services.categories.maintenance' },
    { id: 'waterTreatment', name: t('services.categories.waterTreatment'), icon: <FaWater />, labelKey: 'services.categories.waterTreatment' },
    { id: 'pools', name: t('services.categories.pools'), icon: <FaSwimmingPool />, labelKey: 'services.categories.pools' },
    { id: 'renovation', name: t('services.categories.renovation'), icon: <FaTools />, labelKey: 'services.categories.renovation' }
  ];
  
  // Project data from external source
  const projects: Project[] = createProjectsData();

  const getImageSrc = (imagePath: string) => {
    try {
      return require(`../../../assets/images/projects/${imagePath}`);
    } catch (e) {
      console.warn(`Image not found: ${imagePath}. Using default image.`);
      return require("../../../assets/images/no-image.webp");
    }
  };

  // Filter projects based on category and search query
  useEffect(() => {
    let filtered = projects;
    
    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(project => project.category === activeCategory);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const lang = i18n.language;
      filtered = filtered.filter(project => 
        project.translations[lang]?.title.toLowerCase().includes(query) || 
        project.translations[lang]?.description.toLowerCase().includes(query) ||
        project.translations[lang]?.client.toLowerCase().includes(query) ||
        project.translations[lang]?.location.toLowerCase().includes(query)
      );
    }
    
    setFilteredProjects(filtered);
  }, [activeCategory, searchQuery, i18n.language, projects]);

  
  return (
    <>
      <Seo
        title={pageMeta.projects.title}
        description={pageMeta.projects.description}
        canonical={getCanonicalUrl(pageMeta.projects.canonicalPath)}
      />
      <PageHeader 
        title={t('projects.title')}
        subtitle={t('projects.subtitle')}
        background="/images/background/projects-bg.webp"
      />
      
      <ProjectsSection>
        <div className="container">
          <FilterContainer>
            <CategoryFilter>
              <FilterIcon>
                <FaFilter />
              </FilterIcon>
              <FilterText>{t('projects.filterByCategory')}</FilterText>
              <CategoryButtons>
                {categories.map(category => (
                  <CategoryButton 
                    key={category.id}
                    active={activeCategory === category.id}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    <CategoryIcon>{category.icon}</CategoryIcon>
                    {category.name}
                  </CategoryButton>
                ))}
              </CategoryButtons>
            </CategoryFilter>
            
            <SearchContainer>
              <SearchIcon>
                <FaSearch />
              </SearchIcon>
              <SearchInput 
                type="text" 
                placeholder={t('projects.searchPlaceholder')} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </FilterContainer>
          
          {filteredProjects.length > 0 ? (
            <ProjectsGrid>
              {filteredProjects.map((project, index) => (
                <motion.div 
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <ProjectCard 
                    onClick={() => window.location.href = `/projects/${project.id}`}
                    role="button"
                    tabIndex={0}
                    style={{cursor: 'pointer'}}
                  >

                    <ProjectImageContainer>
                      {/*<ProjectImage src={project.image}*/}
                      {/*              alt={project.translations[i18n.language]?.title || project.translations['en'].title}/>*/}

                      <div className="project-image">
                        <img
                            src={getImageSrc(project.image)}
                            alt={project.translations[i18n.language]?.title || project.translations['fr'].title}
                            className="w-full h-full object-cover rounded-t-lg"
                        />
                      </div>
                      <ProjectCategory>{t(`services.categories.${project.category}`)}</ProjectCategory>
                      {/*{project.featured && <FeaturedBadge>{t('projects.featured')}</FeaturedBadge>}*/}
                    </ProjectImageContainer>

                    <ProjectContent>
                      <ProjectTitle>{project.translations[i18n.language]?.title || project.translations['en'].title}</ProjectTitle>
                      <ProjectMeta>
                        {/*<MetaItem className="project-client">*/}
                        {/*  <MetaLabel>{t('projects.client')}:</MetaLabel>*/}
                        {/*  <MetaValue>{project.translations[i18n.language]?.client || project.translations['en'].client}</MetaValue>*/}
                        {/*</MetaItem>*/}
                        <MetaItem className="project-location">
                          <MetaLabel>{t('projects.location')}:</MetaLabel>
                          <MetaValue>{project.translations[i18n.language]?.location || project.translations['en'].location}</MetaValue>
                        </MetaItem>
                        <MetaItem className="project-year">
                          <MetaLabel>{t('projects.year')}:</MetaLabel>
                          <MetaValue>{project.year}</MetaValue>
                        </MetaItem>
                      </ProjectMeta>
                      <ProjectDescription>{project.translations[i18n.language]?.description || project.translations['en'].description}</ProjectDescription>

                    </ProjectContent>
                  </ProjectCard>
                </motion.div>
              ))}
            </ProjectsGrid>
          ) : (
            <NoResults>
              <NoResultsIcon>
                <FaSearch />
              </NoResultsIcon>
              <NoResultsTitle>{t('projects.noResults')}</NoResultsTitle>
              <NoResultsText>{t('projects.noResultsMessage')}</NoResultsText>
              <ResetButton 
                onClick={() => {
                  setActiveCategory('all');
                  setSearchQuery('');
                }}
              >
                {t('projects.resetFilters')}
              </ResetButton>
            </NoResults>
          )}


        {/*  newspaper*/}
          <Newsletter></Newsletter>

        </div>

      </ProjectsSection>
      

      
    </>
  );
};

// Styled components
const ProjectsSection = styled.section`
  //padding: 100px 0;
  background-color: var(--light);
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 50px;
  flex-wrap: wrap;
  gap: 20px;
  
  @media (max-width: 992px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CategoryFilter = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  
  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

const FilterIcon = styled.div`
  color: var(--primary);
  padding-right: 15px;
  font-size: 1.2rem;
`;

const FilterText = styled.span`
  font-weight: 500;
  color: var(--gray-dark);
`;

const CategoryButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const CategoryButton = styled.button<CategoryButtonProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 15px;
  border: none;
  border-radius: 5px;
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--white)'};
  color: ${props => props.active ? 'white' : 'var(--gray)'};
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--light-gray)'};
  }
`;

const CategoryIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 15px 12px 45px;
  border: 1px solid var(--light-gray);
  border-radius: 5px;
  background-color: var(--white);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: var(--transition);
  cursor: pointer;
  position: relative;
  
  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-5px);
  }

  &:focus {
    outline: 3px solid var(--primary);
    outline-offset: 2px;
  }
`;

const ProjectImageContainer = styled.div`
  position: relative;
  height: 250px;
  overflow: hidden;

  .project-image img {
    transition: transform 0.3s ease;
  }

  ${ProjectCard}:hover .project-image img {
    transform: scale(1.1);
  }
`;

const ProjectCategory = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  padding: 8px 15px;
  background-color: var(--primary);
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const ProjectContent = styled.div`
  padding: 30px;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--primary-dark);
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const MetaLabel = styled.span`
  font-weight: 600;
  color: var(--gray-dark);
`;

const MetaValue = styled.span`
  color: var(--gray);
`;

const ProjectDescription = styled.p`
  color: var(--gray);
  line-height: 1.6;
  margin-bottom: 20px;
`;

const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 86, 118, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;
  border-radius: var(--border-radius);

  ${ProjectCard}:hover & {
    opacity: 1;
  }

  ${ProjectCard}:focus-within & {
    opacity: 1;
  }
`;

const OverlayContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: white;
  text-align: center;
`;

const OverlayIcon = styled.div`
  font-size: 2rem;
`;

const OverlayText = styled.span`
  font-size: 1rem;
  font-weight: 600;
`;

const ProjectServiceLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    color: var(--primary-dark);
    gap: 12px;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const NoResultsIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
`;

const NoResultsTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--primary-dark);
`;

const NoResultsText = styled.p`
  color: var(--gray);
  max-width: 500px;
  margin: 0 auto 30px;
`;

const ResetButton = styled.button`
  padding: 12px 25px;
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;





export default Projects;
